import React from "react"
import { Alert, Collapse } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { DummyReport } from "types/dummyData.types"

interface Props {
  report: DummyReport
  importWarnings: string[]
  onRegenerateMap: () => void
  /** A new map is being made: Regenerate map shows it and takes no click. */
  isRegenerating?: boolean
  /** A new map was made since this run: the run's map findings are out of date. */
  mapRegenerated?: boolean
  /** The project's data is still dummy data: Download CSV is offered only then. */
  canDownloadCsv?: boolean
  onDownloadCsv: () => void
  onDownloadMap: () => void
  onGoToTables: () => void
  onGoToDashboards: () => void
}

/** A count as the user's locale writes it: 1,500 rather than 1500. */
const count = (n: number): string => n.toLocaleString()

/**
 * The generator's notes on exact counts (on a routed question, on a grid,
 * per variant): information the user asked for by setting Exact, so shown
 * even though they are not warnings. Other information stays hidden.
 */
const SHOWN_INFO = new Set(["exact_on_routed", "exact_on_grid", "exact_partial_variants"])

/** "1 answer was" / "3 answers were". */
const counted = (n: number, one: string, many: string): string =>
  `${count(n)} ${n === 1 ? one : many}`

/**
 * What a dummy data run did: completes, replaced terminates, off-path
 * answers, how many respondents each question reached, a stale map (with
 * Regenerate map; once a new map is made, a line saying so), the generator's
 * warnings and notes on exact counts, settings it ignored and the import
 * warnings. Then the downloads and the links to Tables and Dashboards.
 */
const DummyRunReport: React.FC<Props> = ({
  report,
  importWarnings,
  onRegenerateMap,
  isRegenerating = false,
  mapRegenerated = false,
  canDownloadCsv = false,
  onDownloadCsv,
  onDownloadMap,
  onGoToTables,
  onGoToDashboards,
}) => {
  const stale =
    report.unmappedQuestions.length > 0 || report.unfilledMapRows.length > 0
  const notes = report.diagnostics.filter(
    (d) => d.severity !== "info" || SHOWN_INFO.has(d.code)
  )
  return (
    <div className="flex flex-col gap-3" data-testid="dummy-run-report">
      <p className="m-0 text-sm">
        {count(report.completes)} of {count(report.respondents)} respondents
        generated (seed {report.seed}).
        {report.terminatesReplaced > 0 &&
          ` ${counted(report.terminatesReplaced, "terminated attempt was", "terminated attempts were")} replaced.`}
        {report.offPathTotal > 0 &&
          ` ${counted(report.offPathTotal, "off-path answer was", "off-path answers were")} added.`}
      </p>
      {mapRegenerated ? (
        <Alert type="info" showIcon message="Map regenerated. Generate again to use it." />
      ) : stale && (
        <Alert
          type="warning"
          showIcon
          message="The map does not match the questionnaire"
          description={
            <>
              {report.unmappedQuestions.length > 0 && (
                <p className="m-0">
                  Not in the map: {report.unmappedQuestions.join(", ")}
                </p>
              )}
              {report.unfilledMapRows.length > 0 && (
                <p className="m-0">
                  In the map but not in the questionnaire:{" "}
                  {report.unfilledMapRows.join(", ")}
                </p>
              )}
            </>
          }
          action={
            <DataQButton
              isLoading={isRegenerating}
              isDisabled={isRegenerating}
              onClick={onRegenerateMap}
            >
              Regenerate map
            </DataQButton>
          }
        />
      )}
      {report.unusedSettings.length > 0 && (
        <Alert
          type="info"
          showIcon
          message={`These settings no longer match the questionnaire and were ignored: ${report.unusedSettings.join(", ")}`}
        />
      )}
      {notes.map((d, i) => (
        <Alert
          key={`${d.code}-${d.where}-${i}`}
          type={d.severity === "info" ? "info" : "warning"}
          showIcon
          message={d.message}
          description={d.where || undefined}
        />
      ))}
      {importWarnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Import warnings (${importWarnings.length})`}
          description={
            <div className="max-h-48 overflow-y-auto">
              {importWarnings.map((w, i) => (
                <p key={i} className="m-0">
                  {w}
                </p>
              ))}
            </div>
          }
        />
      )}
      {report.asked.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              key: "asked",
              label: "Respondents asked each question",
              children: (
                <ul
                  className="m-0 p-0 list-none text-sm max-h-64 overflow-y-auto"
                  data-testid="dummy-asked-counts"
                >
                  {report.asked.map((a) => (
                    <li key={a.path} className="flex justify-between gap-4">
                      <span>{a.path}</span>
                      <span>{count(a.count)}</span>
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
      )}
      <div className="flex flex-wrap gap-2">
        {canDownloadCsv && (
          <DataQButton onClick={onDownloadCsv}>Download CSV</DataQButton>
        )}
        <DataQButton onClick={onDownloadMap}>Download map</DataQButton>
        <DataQButton type="primary" onClick={onGoToTables}>
          Go to Tables
        </DataQButton>
        <DataQButton onClick={onGoToDashboards}>Go to Dashboards</DataQButton>
      </div>
    </div>
  )
}

export default DummyRunReport
